import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ChevronRight, Pin, X } from 'lucide-react';
import { useShortcuts } from '../contexts/ShortcutsContext';
import usePermissions from '../hooks/usePermissions';
import { resolveShortcutIcon, shortcutColorClass } from '../constants/shortcutTargets';

/**
 * One scrollable row of the user's pinned shortcuts. Sits under the app
 * header while the page is at the top and sticks to the very top of the
 * viewport once the user scrolls (plain CSS `sticky`, no scroll listener).
 */
const ShortcutBar = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { shortcuts, barEnabled, setBarEnabled } = useShortcuts();
  const { canAny } = usePermissions();

  if (!shortcuts.length) return null;

  if (!barEnabled) {
    return (
      <div className="sticky top-0 z-30 -mt-4 flex justify-end">
        <button type="button" onClick={() => setBarEnabled(true)}
          className="flex items-center gap-1 rounded-b-md border border-t-0 border-slate-200 bg-white/90 px-2 py-1 text-[11px] text-slate-500 shadow-sm backdrop-blur hover:text-slate-800"
          title={t('shortcuts.showBar')} data-testid="shortcut-bar-show">
          <Pin className="w-3 h-3" />
          <ChevronRight className="w-3 h-3 rotate-90" />
        </button>
      </div>
    );
  }

  return (
    <div
      className="sticky top-0 z-30 -mx-4 sm:-mx-6 lg:-mx-8 border-b border-slate-200 bg-white/85 px-4 sm:px-6 lg:px-8 py-1.5 backdrop-blur-md"
      data-testid="shortcut-bar"
    >
      <div className="flex items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:h-1">
          {shortcuts.map((item) => {
            const allowed = !item.perms?.length || canAny(...item.perms);
            const Icon = resolveShortcutIcon(item.icon);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  if (!allowed) { toast.error(t('shortcuts.noAccess')); return; }
                  onNavigate(item.tab, { ...(item.context || {}), _n: Date.now() });
                }}
                title={item.subtitle ? `${item.label} · ${item.subtitle}` : item.label}
                className={`flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                  allowed
                    ? 'border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                    : 'border-amber-200 bg-amber-50 text-amber-700'
                }`}
                data-testid={`shortcut-chip-${item.id}`}
              >
                <span className={`inline-flex h-4 w-4 items-center justify-center rounded ${shortcutColorClass(item.color)}`}>
                  <Icon style={{ width: 11, height: 11 }} />
                </span>
                <span className="max-w-[14ch] truncate font-medium">{item.label}</span>
                {item.subtitle && (
                  <span className="hidden max-w-[16ch] truncate text-slate-400 sm:inline">{item.subtitle}</span>
                )}
              </button>
            );
          })}
        </div>
        <button type="button" onClick={() => setBarEnabled(false)}
          className="shrink-0 rounded p-1 text-slate-400 hover:text-slate-700"
          title={t('shortcuts.hideBar')} data-testid="shortcut-bar-hide">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default ShortcutBar;
